import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import AppShell from "../components/AppShell.jsx";
import { apiFetch, getStoredUser } from "../api/api.js";
import { addTerrainLayer, SATELLITE_STYLE } from "../utils/mapTerrain.js";

// ─── useMediaQuery ────────────────────────────────────────────────────────────
function useMediaQuery(q) {
  const [v, setV] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const fn = () => setV(m.matches);
    m.addEventListener("change", fn);
    return () => m.removeEventListener("change", fn);
  }, [q]);
  return v;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const CROP_CATEGORIES = [
  { value: "GRAINS",     label: "Зернові",    icon: "🌾", color: "#f59e0b" },
  { value: "OILSEEDS",   label: "Олійні",     icon: "🌻", color: "#f97316" },
  { value: "LEGUMES",    label: "Бобові",     icon: "🫘", color: "#60a5fa" },
  { value: "VEGETABLES", label: "Овочі",      icon: "🥦", color: "#22c55e" },
  { value: "FRUITS",     label: "Фрукти",     icon: "🍎", color: "#f43f5e" },
  { value: "INDUSTRIAL", label: "Технічні",   icon: "🌿", color: "#a3e635" },
  { value: "FORAGE",     label: "Кормові",    icon: "🌱", color: "#34d399" },
  { value: "OTHER",      label: "Інше",       icon: "📦", color: "#94a3b8" },
];

const BLOCKED_CROP_NAMES = new Set(["Industrial hemp", "Tobacco"]);

const CURRENT_YEAR = new Date().getFullYear();

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getCropCat(v) {
  return CROP_CATEGORIES.find(c => c.value === v)
    ?? { value: v, label: v ?? "—", icon: "🌱", color: "#94a3b8" };
}

function parseMaybeJson(v) {
  if (!v) return null;
  if (typeof v === "object") return v;
  try { return JSON.parse(v); } catch { return null; }
}

function extractFieldGeometry(f) {
  return (
    f?.geometry ??
    parseMaybeJson(f?.geometryJson) ??
    parseMaybeJson(f?.contour) ??
    parseMaybeJson(f?.shape) ??
    null
  );
}

function getFieldCenter(geo) {
  if (!geo) return [31, 48];
  const g = geo.type === "Feature" ? geo.geometry : geo;
  let coords = [];
  if (g?.type === "Polygon")      coords = g.coordinates?.flat(1) ?? [];
  if (g?.type === "MultiPolygon") coords = g.coordinates?.flat(2) ?? [];
  if (!coords.length) return [31, 48];
  const lngs = coords.map(c => c[0]);
  const lats  = coords.map(c => c[1]);
  return [(Math.min(...lngs) + Math.max(...lngs)) / 2, (Math.min(...lats) + Math.max(...lats)) / 2];
}

function initToken() {
  if (!mapboxgl.accessToken) {
    const t = process.env.REACT_APP_MAPBOX_TOKEN || "";
    if (t) mapboxgl.accessToken = t;
  }
  return !!mapboxgl.accessToken;
}

function zoneToBboxGeom(z) {
  const { bboxMinLng: w, bboxMaxLng: e, bboxMinLat: s, bboxMaxLat: n } = z;
  if (w == null || e == null || s == null || n == null) return null;
  return { type: "Polygon", coordinates: [[[w, s], [e, s], [e, n], [w, n], [w, s]]] };
}

// Prefer actual drawn polygon; fall back to bbox rectangle
function zoneToGeom(z) {
  if (!z) return null;
  const raw =
    z.geometryJson ?? z.geometry_json ?? z.geoJson ?? z.geojson ??
    z.geometry ?? z.shape ?? z.contour ?? z.zoneGeometry ?? z.zone_geometry;
  const gj = parseMaybeJson(raw);
  if (gj) {
    const g = gj.type === "Feature" ? gj.geometry : gj;
    if (g?.type && g?.coordinates) return g;
  }
  return zoneToBboxGeom(z);
}

function zoneId(z) { return z?.id ?? z?.zoneId ?? z?.yieldZoneId; }
function zoneKey(z) { return String(zoneId(z) ?? z.name); }

function parseZoneName(name) {
  const m = /^(\d{4})\s*\/\s*(.+)$/.exec((name ?? "").trim());
  if (!m) return { year: null, cropName: (name ?? "").trim() };
  return { year: Number(m[1]), cropName: m[2].trim() };
}

function cropDisplayName(c) {
  return c?.name ?? c?.nameUa ?? c?.nameEn ?? "—";
}

// ─── YieldMapView ─────────────────────────────────────────────────────────────

function YieldMapView({ fieldGeoJson, zones, hiddenZoneIds, selectedZoneId, onSelectZone }) {
  const ref    = useRef(null);
  const mapRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!ref.current || !fieldGeoJson) return;
    if (!initToken()) return;

    const map = new mapboxgl.Map({
      container: ref.current,
      style: SATELLITE_STYLE,
      center: getFieldCenter(fieldGeoJson),
      zoom: 13,
    });
    mapRef.current = map;

    map.on("load", () => {
      addTerrainLayer(map);

      map.addSource("yv-field", { type: "geojson", data: fieldGeoJson });
      map.addLayer({ id: "yv-field-fill", type: "fill",  source: "yv-field", paint: { "fill-color": "#22c55e", "fill-opacity": 0.07 } });
      map.addLayer({ id: "yv-field-line", type: "line",  source: "yv-field", paint: { "line-color": "#22c55e", "line-width": 2.5 } });

      map.addSource("yv-zones", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addLayer({ id: "yv-zones-fill", type: "fill", source: "yv-zones",
        paint: { "fill-color": ["get", "color"], "fill-opacity": 0.28 } });
      map.addLayer({ id: "yv-zones-line", type: "line", source: "yv-zones",
        paint: { "line-color": ["get", "color"], "line-width": 2 } });

      map.on("click", "yv-zones-fill", e => {
        const k = e.features?.[0]?.properties?.key;
        if (k != null) onSelectZone?.(k);
      });
      map.on("mouseenter", "yv-zones-fill", () => { map.getCanvas().style.cursor = "pointer"; });
      map.on("mouseleave", "yv-zones-fill", () => { map.getCanvas().style.cursor = ""; });

      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
      setReady(true);
    });

    return () => {
      setReady(false);
      try { map.remove(); } catch {}
      mapRef.current = null;
    };
  }, [fieldGeoJson]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update zones layer (excludes hidden)
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const src = mapRef.current.getSource("yv-zones");
    if (!src) return;
    let idx = 1;
    const features = zones
      .filter(z => !hiddenZoneIds?.has(zoneKey(z)))
      .map(z => {
        const geom = zoneToGeom(z);
        if (!geom) return null;
        const cat  = getCropCat(z.cropCategory);
        const { cropName } = parseZoneName(z.name);
        const n = idx++;
        return { type: "Feature", geometry: geom, properties: {
          key: zoneKey(z), color: cat.color,
          label: `${n}. ${cat.icon} ${cropName || z.name}`,
        }};
      }).filter(Boolean);
    src.setData({ type: "FeatureCollection", features });
  }, [zones, hiddenZoneIds, ready]);

  // Fit map to show ALL zones when zones load/change (not on visibility toggle)
  useEffect(() => {
    if (!ready || !mapRef.current || zones.length === 0) return;
    const allCoords = zones
      .map(z => zoneToGeom(z))
      .filter(Boolean)
      .flatMap(g => g.coordinates[0]);
    if (!allCoords.length) return;
    const lngs = allCoords.map(c => c[0]);
    const lats  = allCoords.map(c => c[1]);
    try {
      mapRef.current.fitBounds(
        [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
        { padding: 80, maxZoom: 17, duration: 500 }
      );
    } catch {}
  }, [zones, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  // Selected zone highlight: remove/re-add approach (no fitBounds — all zones stay visible)
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const map = mapRef.current;
    ["yv-sel-fill", "yv-sel-line"].forEach(id => {
      try { if (map.getLayer(id)) map.removeLayer(id); } catch {}
    });
    try { if (map.getSource("yv-sel")) map.removeSource("yv-sel"); } catch {}

    if (!selectedZoneId) return;
    const zone = zones.find(z => zoneKey(z) === selectedZoneId);
    if (!zone) return;
    const geom = zoneToGeom(zone);
    if (!geom) return;

    const color = getCropCat(zone.cropCategory).color;
    map.addSource("yv-sel", { type: "geojson", data: {
      type: "FeatureCollection",
      features: [{ type: "Feature", geometry: geom, properties: { color } }],
    }});
    map.addLayer({ id: "yv-sel-fill", type: "fill", source: "yv-sel",
      paint: { "fill-color": color, "fill-opacity": 0.55 } });
    map.addLayer({ id: "yv-sel-line", type: "line", source: "yv-sel",
      paint: { "line-color": color, "line-width": 4 } });
  }, [selectedZoneId, zones, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div ref={ref} style={{ width: "100%", height: "100%", borderRadius: 12, overflow: "hidden", border: "1px solid var(--border)" }} />
  );
}

// ─── DrawBboxMap ──────────────────────────────────────────────────────────────

function DrawBboxMap({ fieldGeoJson, onBboxChange, accentColor, existingZones }) {
  const ref      = useRef(null);
  const mapRef   = useRef(null);
  const loadedRef = useRef(false);

  // Build GeoJSON FeatureCollection from zones array
  function buildZoneFeatures(zones) {
    let idx = 1;
    return (Array.isArray(zones) ? zones : []).map(z => {
      const geom = zoneToGeom(z);
      if (!geom) return null;
      const cat = getCropCat(z.cropCategory);
      const { cropName } = parseZoneName(z.name);
      return { type: "Feature", geometry: geom, properties: {
        color: cat.color, label: `${idx++}. ${cropName || z.name}`,
      }};
    }).filter(Boolean);
  }

  // Safely update ybd-zones source/layers on an already-loaded map
  function applyZones(map, zones) {
    const features = buildZoneFeatures(zones);
    const src = map.getSource("ybd-zones");
    if (src) {
      src.setData({ type: "FeatureCollection", features });
    } else {
      map.addSource("ybd-zones", { type: "geojson", data: { type: "FeatureCollection", features } });
      map.addLayer({ id: "ybd-zones-fill", type: "fill", source: "ybd-zones", paint: { "fill-color": ["get", "color"], "fill-opacity": 0.45 } });
      map.addLayer({ id: "ybd-zones-line", type: "line", source: "ybd-zones", paint: { "line-color": ["get", "color"], "line-width": 2.5 } });
    }
  }

  useEffect(() => {
    if (!ref.current) return;
    if (!initToken()) return;
    const color = accentColor || "#f59e0b";
    const center = fieldGeoJson ? getFieldCenter(fieldGeoJson) : [31, 48];
    const map = new mapboxgl.Map({ container: ref.current, style: SATELLITE_STYLE, center, zoom: 14 });
    mapRef.current  = map;
    loadedRef.current = false;

    const draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: { polygon: true, trash: true },
      styles: [
        { id: "gl-draw-polygon-fill",   type: "fill",   filter: ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]], paint: { "fill-color": color, "fill-opacity": 0.25 } },
        { id: "gl-draw-polygon-stroke", type: "line",   filter: ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]], paint: { "line-color": color, "line-width": 2.5 } },
        { id: "gl-draw-midpoint",       type: "circle", filter: ["all", ["==", "$type", "Point"],   ["==", "meta", "midpoint"]], paint: { "circle-radius": 4, "circle-color": color } },
        { id: "gl-draw-point-outer",    type: "circle", filter: ["all", ["==", "$type", "Point"],   ["!=", "meta", "midpoint"]], paint: { "circle-radius": 6, "circle-color": "#fff", "circle-stroke-width": 2, "circle-stroke-color": color } },
      ],
    });
    map.addControl(draw, "top-left");
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");

    map.on("load", () => {
      addTerrainLayer(map);
      if (fieldGeoJson) {
        map.addSource("ybd-field", { type: "geojson", data: fieldGeoJson });
        map.addLayer({ id: "ybd-fill", type: "fill", source: "ybd-field", paint: { "fill-color": "#22c55e", "fill-opacity": 0.08 } });
        map.addLayer({ id: "ybd-line", type: "line", source: "ybd-field", paint: { "line-color": "#22c55e", "line-width": 2 } });
      }

      // Render initial existing zones
      const zones = Array.isArray(existingZones) ? existingZones : [];
      applyZones(map, zones);

      loadedRef.current = true;
    });

    function emit() {
      const fc = draw.getAll();
      const features = fc?.features ?? [];
      if (!features.length) { onBboxChange(null); return; }
      const geom = features[0]?.geometry;
      if (!geom) { onBboxChange(null); return; }
      const allCoords = geom.type === "Polygon"      ? geom.coordinates.flat(1) :
                        geom.type === "MultiPolygon" ? geom.coordinates.flat(2) : [];
      if (!allCoords.length) { onBboxChange(null); return; }
      const lngs = allCoords.map(c => c[0]);
      const lats  = allCoords.map(c => c[1]);
      onBboxChange({
        bboxMinLng:   Math.min(...lngs),
        bboxMaxLng:   Math.max(...lngs),
        bboxMinLat:   Math.min(...lats),
        bboxMaxLat:   Math.max(...lats),
        geometryJson: JSON.stringify(geom),
      });
    }
    map.on("draw.create", (e) => {
      // Only one zone allowed — delete any previously drawn polygons
      const newId = e.features[0]?.id;
      const toDelete = draw.getAll().features.filter(f => f.id !== newId).map(f => f.id);
      if (toDelete.length) draw.delete(toDelete);
      emit();
    });
    map.on("draw.update", emit);
    map.on("draw.delete", () => onBboxChange(null));

    return () => {
      loadedRef.current = false;
      mapRef.current = null;
      try { map.remove(); } catch {}
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reactively update existing zones layer when prop changes (show/hide toggle)
  useEffect(() => {
    if (!loadedRef.current || !mapRef.current) return;
    try { applyZones(mapRef.current, existingZones); } catch {}
  }, [existingZones]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ position: "relative" }}>
      <div ref={ref} style={{ width: "100%", height: 300, borderRadius: 12, overflow: "hidden" }} />
      <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
        🖊 Намалюйте полігон на вільній ділянці — вже існуючі зони виділено кольором
      </div>
    </div>
  );
}

// ─── CropPickerYield ──────────────────────────────────────────────────────────
// Compact two-level picker: category grid → filtered crop list (no images)

function CropPickerYield({ crops, cropsLoading, category, onCategory, cropId, onCropId }) {
  const availableCats = useMemo(() => {
    const set = new Set();
    (Array.isArray(crops) ? crops : []).forEach(c => {
      const k = String(c?.category ?? "").trim();
      if (k && CROP_CATEGORIES.some(cc => cc.value === k)) set.add(k);
    });
    return CROP_CATEGORIES.filter(cc => set.has(cc.value));
  }, [crops]);

  const filtered = useMemo(() => {
    if (!category) return [];
    return (Array.isArray(crops) ? crops : []).filter(c =>
      String(c?.category || "") === category &&
      !BLOCKED_CROP_NAMES.has(c?.nameEn ?? c?.name_en ?? "")
    );
  }, [crops, category]);

  const selectedCrop = useMemo(() =>
    (Array.isArray(crops) ? crops : []).find(c => String(c?.id) === String(cropId ?? "")),
  [crops, cropId]);

  return (
    <div>
      {/* Category grid */}
      <div className="yp-label" style={{ marginBottom: 7 }}>Категорія <span style={{ color: "#ef4444" }}>*</span></div>
      {cropsLoading ? (
        <div style={{ fontSize: 12, color: "var(--muted)", padding: "8px 0" }}>Завантаження...</div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
          {availableCats.map(cat => {
            const active = category === cat.value;
            return (
              <button key={cat.value} type="button"
                onClick={() => { onCategory(cat.value); onCropId(""); }}
                style={{
                  padding: "6px 12px", borderRadius: 9, fontSize: 12, fontWeight: 700, cursor: "pointer",
                  border: active ? `2px solid ${cat.color}` : "2px solid transparent",
                  background: active ? `${cat.color}18` : "var(--surface-soft,#f5f5f5)",
                  color: active ? cat.color : "var(--muted,#6b7280)", transition: "all .12s",
                }}
              >{cat.icon} {cat.label}</button>
            );
          })}
        </div>
      )}

      {/* Crops list */}
      <div className="yp-label" style={{ marginBottom: 7 }}>
        Культура <span style={{ color: "#ef4444" }}>*</span>
        {selectedCrop && (
          <span style={{ marginLeft: 8, fontWeight: 600, color: "var(--text)", fontSize: 12 }}>
            → {cropDisplayName(selectedCrop)}
          </span>
        )}
      </div>
      {!category ? (
        <div style={{ fontSize: 12, color: "var(--muted)", padding: "6px 0" }}>Спочатку оберіть категорію вище</div>
      ) : cropsLoading ? (
        <div style={{ fontSize: 12, color: "var(--muted)", padding: "6px 0" }}>Завантаження культур...</div>
      ) : filtered.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--muted)", padding: "6px 0" }}>Немає культур у цій категорії</div>
      ) : (
        <div style={{
          display: "flex", flexWrap: "wrap", gap: 6,
          maxHeight: 140, overflowY: "auto", padding: "2px 0",
        }}>
          {filtered.map(c => {
            const id = String(c?.id);
            const active = id === String(cropId ?? "");
            const name = cropDisplayName(c);
            const cat  = getCropCat(c.category);
            return (
              <button key={id} type="button"
                onClick={() => onCropId(id)}
                title={c?.nameEn || name}
                style={{
                  padding: "5px 11px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
                  border: active ? `1.5px solid ${cat.color}` : "1.5px solid var(--border,#e5e7eb)",
                  background: active ? `${cat.color}18` : "var(--surface-soft,#f9fafb)",
                  color: active ? cat.color : "var(--text,#374151)", transition: "all .12s",
                  display: "flex", alignItems: "center", gap: 5,
                }}
              >
                {active && <span style={{ fontSize: 9 }}>✓</span>}
                {name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── ZoneCard ──────────────────────────────────────────────────────────────────

function ZoneCard({ zone, index, selected, hidden, onSelect, onToggleVisible, onDelete }) {
  const cat  = getCropCat(zone.cropCategory);
  const ha   = Number(zone.ha ?? 0);
  const { cropName } = parseZoneName(zone.name);
  const hasBbox = zoneToGeom(zone) != null;

  return (
    <div
      onClick={() => !hidden && onSelect(zoneKey(zone))}
      style={{
        border:       `1.5px solid ${selected ? cat.color : "var(--border,#e5e7eb)"}`,
        borderLeft:   `4px solid ${hidden ? "var(--border,#e5e7eb)" : cat.color}`,
        borderRadius: 10,
        background:   hidden ? "var(--surface-soft,#f9fafb)" : selected ? `${cat.color}0e` : "var(--panel,#fff)",
        padding:      "10px 12px",
        cursor:       hidden ? "default" : "pointer",
        boxShadow:    selected && !hidden ? `0 0 0 3px ${cat.color}22` : "0 1px 3px rgba(0,0,0,.06)",
        opacity:      hidden ? 0.5 : 1,
        transition:   "all .15s",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {/* Zone number + icon */}
        <div style={{ position: "relative", flexShrink: 0 }}>
          <div style={{
            width: 34, height: 34, borderRadius: 9,
            background: hidden ? "rgba(100,116,139,.08)" : `${cat.color}1a`,
            border: `1.5px solid ${hidden ? "var(--border)" : cat.color + "50"}`,
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
          }}>{cat.icon}</div>
          <div style={{
            position: "absolute", top: -5, right: -5,
            width: 16, height: 16, borderRadius: "50%", fontSize: 9, fontWeight: 900,
            background: hidden ? "var(--border,#e5e7eb)" : cat.color,
            color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
            lineHeight: 1,
          }}>{index}</div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 3 }}>
            <span style={{
              fontSize: 10, padding: "1px 7px", borderRadius: 20, fontWeight: 700,
              background: `${cat.color}18`, border: `1px solid ${cat.color}40`, color: cat.color,
            }}>{cat.label}</span>
            <span style={{
              fontSize: 10, padding: "1px 7px", borderRadius: 20, fontWeight: 600,
              background: "rgba(100,116,139,.08)", border: "1px solid rgba(100,116,139,.20)", color: "var(--muted)",
            }}>📐 {ha.toFixed(2)} га</span>
            {!hasBbox && (
              <span style={{
                fontSize: 9, padding: "1px 6px", borderRadius: 20, fontWeight: 700,
                background: "rgba(245,158,11,.12)", color: "#d97706", border: "1px solid rgba(245,158,11,.25)",
              }}>без bbox</span>
            )}
          </div>
          <div style={{
            fontWeight: 700, fontSize: 13,
            color: hidden ? "var(--muted,#9ca3af)" : "var(--text,#111)",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {cropName || zone.name}
          </div>
        </div>

        {/* Eye toggle */}
        <button
          type="button"
          className="yp-zone-btn"
          onClick={e => { e.stopPropagation(); onToggleVisible(zoneKey(zone)); }}
          title={hidden ? "Показати на карті" : "Сховати з карти"}
          style={{
            background: hidden ? "rgba(100,116,139,.1)" : `${cat.color}12`,
            color: hidden ? "var(--muted,#9ca3af)" : cat.color,
            border: `1px solid ${hidden ? "var(--border)" : cat.color + "30"}`,
            padding: "5px 7px", borderRadius: 9, cursor: "pointer", fontSize: 14, flexShrink: 0, lineHeight: 1,
          }}
        >{hidden ? "👁‍🗨" : "👁"}</button>

        {/* Delete */}
        <button
          type="button"
          className="yp-zone-btn"
          onClick={e => { e.stopPropagation(); onDelete(zone); }}
          style={{
            background: "rgba(239,68,68,.06)", color: "#ef4444",
            border: "1px solid rgba(239,68,68,.18)", padding: "5px 7px",
            borderRadius: 9, cursor: "pointer", fontSize: 14, flexShrink: 0, lineHeight: 1,
          }}
        >🗑</button>
      </div>
    </div>
  );
}

// ─── FieldItem ─────────────────────────────────────────────────────────────────

function FieldItem({ field, active, onClick, isMobile }) {
  const name     = field.name || `Поле #${field.id}`;
  const initials = name.trim().slice(0, 2).toUpperCase();
  const ha       = field.areaHa ? Number(field.areaHa).toFixed(1) : null;
  const hasGeom  = !!extractFieldGeometry(field);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`yp-field-btn${active ? " active" : ""}`}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "9px 10px 9px 8px", width: "100%", textAlign: "left",
        background: active ? "rgba(34,197,94,.09)" : "transparent",
        border: "none",
        borderLeft: `3px solid ${active ? "#22c55e" : "transparent"}`,
        borderRadius: active ? "0 9px 9px 0" : "9px",
        cursor: "pointer",
        transition: "background .12s, border-color .12s",
        flexShrink: 0,
      }}
    >
      <div className="yp-field-avatar" style={{
        width: 36, height: 36, borderRadius: 9, flexShrink: 0,
        background: active ? "#22c55e" : "rgba(100,116,139,.10)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 11, fontWeight: 800, letterSpacing: "-.01em",
        color: active ? "#fff" : "var(--muted,#6b7280)",
        transition: "background .12s, color .12s",
      }}>
        {initials}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="yp-field-name" style={{
          fontSize: 13, fontWeight: 700, lineHeight: 1.25,
          color: active ? "#15803d" : "var(--text,#111)",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{name}</div>
        <div className="yp-field-ha" style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
          {ha && (
            <span style={{ fontSize: 10, color: active ? "#16a34a" : "var(--muted,#9ca3af)", fontWeight: 600 }}>
              {ha} га
            </span>
          )}
          {!hasGeom && (
            <span style={{
              fontSize: 9, padding: "0 5px", borderRadius: 4, fontWeight: 700,
              background: "rgba(245,158,11,.12)", color: "#d97706", border: "1px solid rgba(245,158,11,.25)",
            }}>без карти</span>
          )}
        </div>
      </div>

      {active && !isMobile && (
        <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e", flexShrink: 0 }} />
      )}
    </button>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function YieldPage() {
  const navigate = useNavigate();
  const [user]   = useState(() => getStoredUser());
  const isMobile = useMediaQuery("(max-width: 760px)");

  // Fields
  const [fields,        setFields]        = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [fieldSearch,   setFieldSearch]   = useState("");
  const [selectedField, setSelectedField] = useState(null);

  // Zones
  const [zones,          setZones]          = useState([]);
  const [zonesLoading,   setZonesLoading]   = useState(false);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [hiddenZoneIds,  setHiddenZoneIds]  = useState(new Set());

  // Year filter
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);

  // Crops from API
  const [allCrops,     setAllCrops]     = useState([]);
  const [cropsLoading, setCropsLoading] = useState(false);

  // Modal / saving
  const [modalOpen,   setModalOpen]   = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [confirmZone, setConfirmZone] = useState(null);

  // Form
  const [fYear,              setFYear]              = useState(CURRENT_YEAR);
  const [fCropCategory,      setFCropCategory]      = useState("");
  const [fCropId,            setFCropId]            = useState("");
  const [fName,              setFName]              = useState("");
  const [fHa,                setFHa]                = useState("");
  const [fBbox,              setFBbox]              = useState(null);
  const [showExistingOnDraw, setShowExistingOnDraw] = useState(false);

  // Toast
  const [toast, setToast] = useState(null);
  function showToast(type, msg) {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  }

  // ── Load fields
  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    setFieldsLoading(true);
    apiFetch(user, "/api/fields")
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(raw => {
        const list = Array.isArray(raw) ? raw : (raw?.items ?? raw?.content ?? []);
        list.sort((a, b) => String(a?.name ?? "").localeCompare(String(b?.name ?? ""), "uk"));
        setFields(list);
        if (list.length > 0) setSelectedField(list[0]);
      })
      .catch(() => { setFields([]); showToast("err", "Не вдалось завантажити поля"); })
      .finally(() => setFieldsLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load crops once
  useEffect(() => {
    if (!user) return;
    setCropsLoading(true);
    apiFetch(user, "/api/crop")
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(raw => {
        const list = Array.isArray(raw) ? raw : (raw?.items ?? raw?.content ?? []);
        setAllCrops(list.filter(c => c && !BLOCKED_CROP_NAMES.has(c?.nameEn ?? "")));
      })
      .catch(() => setAllCrops([]))
      .finally(() => setCropsLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load zones when field changes
  const loadZones = useCallback(async () => {
    if (!selectedField?.id || !user) return;
    setZonesLoading(true);
    setZones([]);
    setSelectedZoneId(null);
    setHiddenZoneIds(new Set());
    try {
      const r = await apiFetch(user, `/api/fields/${selectedField.id}/yield-zones`);
      if (!r.ok) return;
      const raw = await r.json();
      const list = Array.isArray(raw) ? raw : (raw?.items ?? []);
      console.log("[loadZones] first zone:", list[0]);
      setZones(list);
    } catch { showToast("err", "Помилка завантаження зон"); }
    finally { setZonesLoading(false); }
  }, [selectedField?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadZones(); }, [loadZones]);

  // ── Year tabs from zones
  const availableYears = useMemo(() => {
    const yearsFromZones = new Set(
      zones.map(z => parseZoneName(z.name).year).filter(Boolean)
    );
    const base = [CURRENT_YEAR - 2, CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1, CURRENT_YEAR + 2];
    base.forEach(y => yearsFromZones.add(y));
    return [...yearsFromZones].sort((a, b) => a - b);
  }, [zones]);

  // ── Zones filtered by selected year
  const zonesForYear = useMemo(() =>
    zones.filter(z => {
      const { year } = parseZoneName(z.name);
      return year === selectedYear || (year == null && selectedYear === CURRENT_YEAR);
    }),
  [zones, selectedYear]);

  // Auto-fill zone name when year/crop changes
  useEffect(() => {
    const crop = allCrops.find(c => String(c?.id) === String(fCropId));
    setFName(crop ? `${fYear} / ${cropDisplayName(crop)}` : fYear ? `${fYear} / ` : "");
  }, [fYear, fCropId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save zone
  async function save(e) {
    e.preventDefault();
    if (!fName.trim())                     { showToast("err", "⚠️ Введіть назву зони");           return; }
    if (!fCropCategory)                    { showToast("err", "⚠️ Оберіть категорію культури");   return; }
    if (!fCropId)                          { showToast("err", "⚠️ Оберіть культуру");             return; }
    if (!fHa || Number(fHa) <= 0)         { showToast("err", "⚠️ Вкажіть площу більше 0 га");   return; }
    if (!fBbox)                            { showToast("err", "⚠️ Намалюйте зону на карті");      return; }

    const zoneName = fName.trim();

    setSaving(true);
    try {
      const body = {
        name:         zoneName,
        ha:           Number(fHa),
        fieldId:      Number(selectedField.id),
        cropCategory: fCropCategory,
        ...fBbox,
      };
      const r = await apiFetch(user, `/api/fields/${selectedField.id}/yield-zones`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const txt = await r.text().catch(() => "");
        let msg = "Не вдалось зберегти зону";
        try { const j = JSON.parse(txt); msg = j.message || j.error || msg; } catch {}
        showToast("err", msg);
        return;
      }
      await loadZones();
      setSelectedYear(fYear);
      resetForm();
      setModalOpen(false);
      showToast("ok", `✅ Зону "${zoneName}" збережено`);
    } catch (err) {
      console.error(err);
      showToast("err", "Помилка збереження");
    } finally { setSaving(false); }
  }

  async function deleteZone(zone) {
    const id = zoneId(zone);
    console.log("[deleteZone] zone object:", zone, "resolved id:", id);
    if (id == null) { showToast("err", "ID зони недоступний"); setConfirmZone(null); return; }
    try {
      const r = await apiFetch(user, `/api/fields/${selectedField.id}/yield-zones/${id}`, { method: "DELETE" });
      if (!r.ok) { showToast("err", `Помилка: ${r.status}`); return; }
      await loadZones();
      showToast("ok", "🗑 Зону видалено");
    } catch { showToast("err", "Помилка видалення"); }
    finally { setConfirmZone(null); }
  }

  function resetForm() {
    setFCropCategory(""); setFCropId(""); setFName(""); setFHa(""); setFBbox(null);
    setFYear(CURRENT_YEAR); setShowExistingOnDraw(false);
  }

  function toggleZoneVisibility(key) {
    setHiddenZoneIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  // ── Derived
  const filteredFields = useMemo(() =>
    fields.filter(f => String(f?.name ?? "").toLowerCase().includes(fieldSearch.toLowerCase())),
  [fields, fieldSearch]);

  const fieldGeoJson = useMemo(() => extractFieldGeometry(selectedField), [selectedField]);
  const totalHa      = useMemo(() => zonesForYear.reduce((s, z) => s + Number(z.ha ?? 0), 0), [zonesForYear]);

  const accentColor  = fCropCategory ? getCropCat(fCropCategory).color : "#f59e0b";

  // Existing zones for the year being created — shown in draw modal so user can see what's occupied
  const zonesForFormYear = useMemo(() =>
    zones.filter(z => {
      const { year } = parseZoneName(z.name);
      return year === fYear || (year == null && fYear === CURRENT_YEAR);
    }),
  [zones, fYear]);

  const zonesForFormYearWithBbox = useMemo(() =>
    zonesForFormYear.filter(z => zoneToGeom(z) != null),
  [zonesForFormYear]);

  const hiddenCount  = useMemo(() => zonesForYear.filter(z => hiddenZoneIds.has(zoneKey(z))).length, [zonesForYear, hiddenZoneIds]);
  const visibleCount = zonesForYear.length - hiddenCount;

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <AppShell
      title="Врожайність"
      subtitle="Оберіть поле та керуйте зонами врожайності"
    >
      <style>{`
        @keyframes ypSpin { to { transform: rotate(360deg); } }
        .yp-spin { width: 28px; height: 28px; border: 3px solid var(--border); border-top: 3px solid #f59e0b; border-radius: 50%; animation: ypSpin .7s linear infinite; }
        .yp-input { width: 100%; box-sizing: border-box; padding: 9px 12px; border: 1.5px solid var(--border); border-radius: 9px; font-size: 16px; min-height: 48px; background: var(--input-bg); color: var(--text); outline: none; transition: border-color .15s, box-shadow .15s; }
        .yp-input:focus { border-color: #f59e0b; box-shadow: 0 0 0 3px rgba(245,158,11,.14); background: var(--input-focus-bg); }
        .yp-input::placeholder { color: var(--muted); opacity: .7; }
        .yp-label { font-size: 12px; font-weight: 700; color: var(--muted); margin-bottom: 5px; }
        .yp-year-tab { padding: 7px 14px; min-height: 44px; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; border: 1.5px solid transparent; transition: all .12s; background: transparent; color: var(--muted); }
        .yp-year-tab.active { background: #f59e0b18; border-color: #f59e0b; color: #d97706; }
        .yp-year-tab:hover:not(.active) { background: var(--surface-soft); }
        .yp-zone-btn { min-height: 44px; min-width: 44px; display: flex; align-items: center; justify-content: center; }
        .yp-main { min-width: 0; }

        /* ── Mobile layout ── */
        @media (max-width: 760px) {
          .yp-layout {
            flex-direction: column !important;
            align-items: stretch !important;
            flex-wrap: nowrap !important;
          }
          .yp-main { width: 100% !important; flex: none !important; min-width: 0 !important; }

          /* Sidebar becomes a compact horizontal field picker */
          .yp-sidebar {
            flex: none !important;
            width: 100% !important;
            min-width: 0 !important;
            max-height: none !important;
            flex-direction: row !important;
            align-items: stretch !important;
          }
          .yp-sidebar-header {
            display: none !important;
          }
          .yp-sidebar-footer {
            display: none !important;
          }
          .yp-sidebar-scroll {
            flex-direction: row !important;
            overflow-x: auto !important;
            overflow-y: hidden !important;
            padding: 8px !important;
            gap: 6px !important;
            flex-wrap: nowrap !important;
            scrollbar-width: none !important;
          }
          .yp-sidebar-scroll::-webkit-scrollbar { display: none; }
          /* Field item pills in horizontal layout */
          .yp-field-btn {
            flex-direction: column !important;
            align-items: center !important;
            gap: 3px !important;
            padding: 8px 10px !important;
            min-width: 64px !important;
            max-width: 80px !important;
            width: auto !important;
            border-radius: 12px !important;
            border: 1.5px solid var(--border) !important;
            text-align: center !important;
          }
          .yp-field-btn.active {
            border-color: #22c55e !important;
            background: rgba(34,197,94,.09) !important;
          }
          .yp-field-btn .yp-field-avatar {
            width: 36px !important; height: 36px !important;
          }
          .yp-field-btn .yp-field-name {
            font-size: 9px !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            max-width: 70px !important;
          }
          .yp-field-btn .yp-field-ha { display: none !important; }

          /* Modal bottom sheet */
          .yp-modal-overlay { align-items: flex-end !important; padding: 0 !important; overflow-y: visible !important; }
          .yp-modal-card {
            width: 100vw !important; max-width: 100vw !important;
            border-radius: 22px 22px 0 0 !important;
            padding: 20px 14px 32px !important;
            max-height: 92vh !important;
            overflow-y: auto;
          }
          .yp-zone-header { flex-direction: column !important; align-items: stretch !important; }
          .yp-zone-header .btn { width: 100% !important; min-height: 48px !important; }
          .yp-modal-actions { flex-direction: column-reverse !important; gap: 8px !important; }
          .yp-modal-actions .btn,
          .yp-modal-actions button { width: 100% !important; min-height: 50px !important; font-size: 15px !important; }
          .yp-confirm-card { width: calc(100vw - 32px) !important; }
        }
      `}</style>

      {/* Toast */}
      {toast && createPortal(
        <div style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 9999,
          padding: "11px 18px", borderRadius: 12, fontWeight: 700, fontSize: 13,
          background: toast.type === "ok" ? "#22c55e" : "#ef4444", color: "#fff",
          boxShadow: "0 4px 20px rgba(0,0,0,.25)",
        }}>{toast.msg}</div>,
        document.body
      )}

      <div style={{ padding: "0 0 40px" }}>

        {/* Layout */}
        <div className="yp-layout" style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>

          {/* Field sidebar */}
          <div className="yp-sidebar" style={{
            flex: "0 0 240px", minWidth: 200,
            background: "var(--panel,#fff)", border: "1px solid var(--border)", borderRadius: 14,
            overflow: "hidden", maxHeight: "82vh", display: "flex", flexDirection: "column",
          }}>
            <div className="yp-sidebar-header" style={{ padding: "13px 14px 10px", borderBottom: "1px solid var(--border)", background: "var(--surface-soft,#f9fafb)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontWeight: 800, fontSize: 13, color: "var(--text)" }}>🌿 Поля</span>
                <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, fontWeight: 700, background: "rgba(34,197,94,.12)", border: "1px solid rgba(34,197,94,.25)", color: "#16a34a" }}>{fields.length}</span>
              </div>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "var(--muted)", pointerEvents: "none" }}>🔍</span>
                <input className="yp-input" value={fieldSearch} onChange={e => setFieldSearch(e.target.value)} placeholder="Пошук поля..." style={{ fontSize: 12, paddingLeft: 26 }} />
              </div>
            </div>

            <div className="yp-sidebar-scroll" style={{ flex: 1, overflowY: "auto", padding: "6px 4px", display: "flex", flexDirection: "column", gap: 0 }}>
              {fieldsLoading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: 24 }}><div className="yp-spin" /></div>
              ) : filteredFields.length === 0 ? (
                <div style={{ textAlign: "center", padding: "20px 0", fontSize: 12, color: "var(--muted)" }}>Поля не знайдено</div>
              ) : filteredFields.map(f => (
                <FieldItem key={f.id} field={f} active={selectedField?.id === f.id} onClick={() => setSelectedField(f)} isMobile={isMobile} />
              ))}
            </div>

            {fields.length > 0 && !fieldsLoading && (
              <div className="yp-sidebar-footer" style={{ padding: "9px 14px", borderTop: "1px solid var(--border)", background: "var(--surface-soft,#f9fafb)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 11, color: "var(--muted)" }}>Загальна площа</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>
                  {fields.reduce((s, f) => s + Number(f.areaHa ?? 0), 0).toFixed(1)} га
                </span>
              </div>
            )}
          </div>

          {/* Main content */}
          <div className="yp-main" style={{ flex: "1 1 400px", minWidth: 0 }}>

            {!selectedField ? (
              <div style={{ textAlign: "center", padding: "70px 20px", border: "1px dashed var(--border)", borderRadius: 14 }}>
                <div style={{ fontSize: 48 }}>🌿</div>
                <p style={{ margin: "10px 0 0", fontWeight: 700, fontSize: 15, color: "var(--text)" }}>Оберіть поле зі списку</p>
              </div>
            ) : <>

              {/* Field info bar */}
              <div style={{
                background: "var(--panel,#fff)", border: "1px solid var(--border)", borderRadius: 14,
                padding: "13px 18px", marginBottom: 14,
                display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 8,
              }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 17, color: "var(--text)" }}>
                    {selectedField.name || `Поле #${selectedField.id}`}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2, display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {selectedField.areaHa && <span>📏 {Number(selectedField.areaHa).toFixed(1)} га</span>}
                    {zonesForYear.length > 0 && (
                      <span style={{ color: "#f59e0b", fontWeight: 700 }}>🌾 {totalHa.toFixed(2)} га в зонах ({selectedYear})</span>
                    )}
                  </div>
                </div>
                {!fieldGeoJson && (
                  <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, fontWeight: 700, background: "rgba(245,158,11,.12)", border: "1px solid rgba(245,158,11,.30)", color: "#d97706" }}>△ Поле без контуру</span>
                )}
              </div>

              {/* Map */}
              {fieldGeoJson && (
                <div style={{ height: isMobile ? 240 : 380, marginBottom: 14, borderRadius: 14, overflow: "hidden", border: "1px solid var(--border)" }}>
                  <YieldMapView
                    fieldGeoJson={fieldGeoJson}
                    zones={zonesForYear}
                    hiddenZoneIds={hiddenZoneIds}
                    selectedZoneId={selectedZoneId}
                    onSelectZone={id => setSelectedZoneId(prev => prev === id ? null : id)}
                  />
                </div>
              )}

              {/* Year tabs */}
              <div style={{
                background: "var(--panel,#fff)", border: "1px solid var(--border)", borderRadius: 12,
                padding: "10px 14px", marginBottom: 12,
                display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
              }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", marginRight: 4 }}>Рік:</span>
                {availableYears.map(y => (
                  <button key={y} type="button"
                    className={`yp-year-tab${selectedYear === y ? " active" : ""}`}
                    onClick={() => { setSelectedYear(y); setSelectedZoneId(null); }}
                  >{y}</button>
                ))}
              </div>

              {/* Zone list header */}
              <div className="yp-zone-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text)" }}>
                  Зони {selectedYear}
                  <span style={{ fontWeight: 400, fontSize: 12, color: "var(--muted)", marginLeft: 8 }}>
                    {zonesLoading ? "Завантаження..." : `${zonesForYear.length} ${zonesForYear.length === 1 ? "зона" : zonesForYear.length < 5 ? "зони" : "зон"}`}
                  </span>
                  {hiddenCount > 0 && (
                    <span style={{ fontSize: 11, marginLeft: 8, color: "#d97706", fontWeight: 600 }}>({visibleCount} видно)</span>
                  )}
                </div>
                <button
                  className="btn primary" type="button"
                  disabled={!selectedField || zonesLoading}
                  onClick={() => { resetForm(); setModalOpen(true); }}
                  style={{ fontWeight: 700, padding: "7px 16px", fontSize: 13 }}
                >+ Додати зону</button>
              </div>

              {/* Zones list */}
              {zonesLoading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}><div className="yp-spin" /></div>
              ) : zonesForYear.length === 0 ? (
                <div style={{ textAlign: "center", padding: "36px 20px", border: "1px dashed var(--border)", borderRadius: 12 }}>
                  <div style={{ fontSize: 38 }}>🌾</div>
                  <p style={{ margin: "8px 0 4px", fontSize: 14, fontWeight: 700, color: "var(--text)" }}>
                    Немає зон за {selectedYear} рік
                  </p>
                  <p style={{ margin: 0, fontSize: 12, color: "var(--muted)" }}>
                    Натисніть «+ Додати зону» щоб позначити ділянку культури
                  </p>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {zonesForYear.map((z, i) => (
                    <ZoneCard
                      key={z.id ?? `${z.name}-${i}`}
                      zone={z}
                      index={i + 1}
                      selected={selectedZoneId === zoneKey(z)}
                      hidden={hiddenZoneIds.has(zoneKey(z))}
                      onSelect={id => setSelectedZoneId(prev => prev === id ? null : id)}
                      onToggleVisible={toggleZoneVisibility}
                      onDelete={zone => setConfirmZone(zone)}
                    />
                  ))}
                </div>
              )}
            </>}
          </div>
        </div>
      </div>

      {/* Create modal */}
      {modalOpen && createPortal(
        <div
          className="yp-modal-overlay"
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.52)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 5000, overflowY: "auto", padding: "24px 16px" }}
          onClick={() => setModalOpen(false)}
        >
          <div
            className="yp-modal-card"
            style={{ background: "var(--modal-bg,var(--panel,#fff))", border: "1px solid var(--border)", borderRadius: 18, padding: "26px 28px", width: "min(700px, 100%)", boxShadow: "0 12px 60px rgba(0,0,0,.28)", color: "var(--text)" }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 18 }}>🌾 Нова зона врожайності</div>
                <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 3 }}>
                  Поле: <strong>{selectedField?.name}</strong>
                </div>
              </div>
              <button className="btn" type="button" onClick={() => setModalOpen(false)}>✕</button>
            </div>

            <form onSubmit={save}>

              {/* Year */}
              <div style={{ marginBottom: 16 }}>
                <div className="yp-label">Рік <span style={{ color: "#ef4444" }}>*</span></div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {[CURRENT_YEAR - 3, CURRENT_YEAR - 2, CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1, CURRENT_YEAR + 2].map(y => (
                    <button key={y} type="button"
                      onClick={() => setFYear(y)}
                      style={{
                        padding: "6px 16px", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer",
                        border: fYear === y ? "2px solid #f59e0b" : "2px solid var(--border,#e5e7eb)",
                        background: fYear === y ? "#f59e0b18" : "var(--surface-soft,#f5f5f5)",
                        color: fYear === y ? "#d97706" : "var(--muted,#6b7280)", transition: "all .12s",
                      }}
                    >{y}</button>
                  ))}
                </div>
              </div>

              {/* Name field */}
              <div style={{ marginBottom: 16 }}>
                <div className="yp-label">
                  Назва зони <span style={{ color: "#ef4444" }}>*</span>
                  <span style={{ marginLeft: 7, fontWeight: 400, fontSize: 11, color: "var(--muted)" }}>
                    (заповнюється автоматично, можна змінити)
                  </span>
                </div>
                <input
                  className="yp-input"
                  value={fName}
                  onChange={e => setFName(e.target.value)}
                  placeholder="Напр.: 2026 / Пшениця озима"
                />
              </div>

              {/* Category + Crops */}
              <div style={{ marginBottom: 16 }}>
                <CropPickerYield
                  crops={allCrops}
                  cropsLoading={cropsLoading}
                  category={fCropCategory}
                  onCategory={setFCropCategory}
                  cropId={fCropId}
                  onCropId={setFCropId}
                />
              </div>

              {/* Ha */}
              <div style={{ marginBottom: 16 }}>
                <div className="yp-label">Площа зони (га) <span style={{ color: "#ef4444" }}>*</span></div>
                <input className="yp-input" type="number" min="0.01" step="0.01" value={fHa}
                  onChange={e => setFHa(e.target.value)} placeholder="Напр.: 5.25" />
              </div>

              {/* Draw map */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <span className="yp-label" style={{ margin: 0 }}>Зона на карті <span style={{ color: "#ef4444" }}>*</span></span>
                    {fBbox
                      ? <span style={{ fontSize: 10, color: "#22c55e", fontWeight: 700 }}>✓ Намальовано</span>
                      : <span style={{ fontSize: 10, color: "#f59e0b", fontWeight: 700 }}>Намалюйте полігон</span>
                    }
                  </div>
                  {fBbox && (
                    <span style={{ fontSize: 10, color: "var(--muted)", fontWeight: 600 }}>
                      🖊 Щоб перемалювати — видаліть і намалюйте знову
                    </span>
                  )}
                </div>

                {/* Toggle existing zones — separate row above map */}
                {zonesForFormYearWithBbox.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowExistingOnDraw(prev => !prev)}
                    style={{
                      width: "100%", marginBottom: 8, padding: "7px 12px",
                      borderRadius: 9, cursor: "pointer", fontWeight: 700, fontSize: 12,
                      border: showExistingOnDraw ? `1.5px solid ${accentColor}` : "1.5px solid var(--border)",
                      background: showExistingOnDraw ? `${accentColor}12` : "var(--surface-soft)",
                      color: showExistingOnDraw ? accentColor : "var(--muted)",
                      transition: "all .15s", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    }}
                  >
                    {showExistingOnDraw
                      ? <><span>🚫</span> Сховати існуючі зони</>
                      : <><span>👁</span> Показати зони {fYear} на карті ({zonesForFormYearWithBbox.length})</>
                    }
                  </button>
                )}

                <DrawBboxMap
                  key={`${fYear}-${fCropCategory}`}
                  fieldGeoJson={fieldGeoJson}
                  onBboxChange={setFBbox}
                  accentColor={accentColor}
                  existingZones={showExistingOnDraw ? zonesForFormYearWithBbox : []}
                />
              </div>

              <div className="yp-modal-actions" style={{ display: "flex", justifyContent: "flex-end", gap: 10, borderTop: "1px solid var(--border)", paddingTop: 16 }}>
                <button className="btn" type="button" onClick={() => setModalOpen(false)} disabled={saving}>Скасувати</button>
                <button
                  type="submit" disabled={saving}
                  style={{
                    padding: "9px 20px", minHeight: 44, borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: "pointer", border: "none",
                    background: saving ? "rgba(100,116,139,.3)" : accentColor, color: "#fff", transition: "all .12s",
                  }}
                >{saving ? "Зберігаю..." : `${getCropCat(fCropCategory || "OTHER").icon} Зберегти зону`}</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Confirm delete */}
      {confirmZone && createPortal(
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.52)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 6000, padding: "16px" }}
          onClick={() => setConfirmZone(null)}
        >
          <div
            className="yp-confirm-card"
            style={{ background: "var(--modal-bg,var(--panel,#fff))", border: "1px solid var(--border)", borderRadius: 16, padding: "24px 28px", width: "min(340px, 100%)", textAlign: "center", boxShadow: "0 8px 40px rgba(0,0,0,.2)", color: "var(--text)" }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: 28, marginBottom: 10 }}>🗑️</div>
            <p style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700 }}>{confirmZone.name}</p>
            <p style={{ margin: "0 0 20px", fontSize: 13, color: "var(--muted)" }}>Видалити цю зону врожайності?</p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
              <button onClick={() => deleteZone(confirmZone)} style={{ background: "#dc2626", color: "#fff", border: "none", padding: "11px 20px", borderRadius: 9, cursor: "pointer", fontSize: 14, fontWeight: 700, minHeight: 44, flex: 1 }}>Так, видалити</button>
              <button onClick={() => setConfirmZone(null)} style={{ background: "transparent", color: "var(--muted)", border: "1px solid var(--border)", padding: "11px 20px", borderRadius: 9, cursor: "pointer", fontSize: 14, minHeight: 44, flex: 1 }}>Скасувати</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </AppShell>
  );
}
